/**
 * This file provides a low-level primitive for creating Zustand stores with:
 * - **Minimal Core** - Returns [Provider, useStore, useSelector]
 * - **Store Owner Control** - Direct access to store instance, actions, getState, subscribe
 * - **Consumer Safety** - Concrete store implementations only export custom hooks not raw access to the store instance.
 * - React Context Provider and hook generation
 * - Immer middleware for immutable updates with mutable syntax
 * - TypeScript inference for full type safety
 * - Per-provider store instances for test isolation
 *
 * ## Philosophy
 *
 * `createStore` is a **low-level primitive**. Store files use it to build a curated API.
 * Consumers never use `createStore` directly - they import custom hooks from store files.
 *
 * **Store Owner** (has power):
 * - Defines state shape
 * - Defines actions
 * - Creates optimized custom hooks for consumers
 * - Has full access to store internals
 *
 * **Consumer** (safe, guided):
 * - Imports custom hooks from store file
 * - Can't write bad selectors
 * - Can't access state incorrectly
 * - Gets optimized hooks from store owner
 *
 * @example
 * ```tsx
 * // ========================================
 * // STORE FILE: counter.store.tsx
 * // ========================================
 *
 * import { createStore } from '@/stores';
 *
 * // Define state shape
 * interface CounterState {
 *   count: number;
 *   name: string;
 * }
 *
 * // Define actions
 * interface CounterActions {
 *   increment: () => void;
 *   decrement: () => void;
 *   setName: (name: string) => void;
 * }
 *
 * // Create store with actions
 * const [CounterProvider, useCounterStore, useCounterSelector] = createStore<CounterState, CounterActions>(
 *   { count: 0, name: 'Counter' },
 *   (set, get, store) => ({
 *     increment: () => set(draft => { draft.count += 1; }),
 *     decrement: () => set(draft => { draft.count -= 1; }),
 *     setName: (name: string) => set(draft => { draft.name = name; }),
 *   })
 * );
 *
 * // Set display name for React DevTools
 * CounterProvider.displayName = 'CounterProvider';
 *
 * // Store owner creates custom hooks (optimized, tested)
 * export const useCount = () => useCounterSelector(state => state.count);
 * export const useName = () => useCounterSelector(state => state.name);
 * export const useCounterActions = () => useCounterStore().actions;
 *
 * // Export provider for consumers
 * export { CounterProvider };
 *
 * // ========================================
 * // CONSUMER: SomeComponent.tsx
 * // ========================================
 *
 * import { CounterProvider, useCount, useCounterActions } from '@/somePath/counter';
 *
 * function Counter() {
 *   const count = useCount(); // Only re-renders when count changes
 *   const { increment } = useCounterActions(); // Never triggers re-renders
 *   return <button onClick={increment}>{count}</button>;
 * }
 *
 * // Wrap with provider
 * <CounterProvider>
 *   <Counter />
 * </CounterProvider>
 * ```
 */

import { createContext, useContext, useState } from 'react';
import { type StateCreator, type StoreApi, create, useStore as useZustandStore } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { ReactElement } from 'react';

/**
 * Set store state function type for Immer-style draft updates
 */
export type SetStoreState<S> = (updater: (draft: S) => void) => void;

/**
 * Get store state function type
 */
export type GetStoreState<S> = () => S;

/**
 * Actions factory function that receives store helpers and returns action functions
 */
export type ActionsFactory<S, A> = (set: SetStoreState<S>, get: GetStoreState<S>, store: StoreApi<S>) => A;

/**
 * Zustand middleware type
 * A middleware is a function that wraps the state creator
 */
export type Middleware<S> = (config: StateCreator<S, [], []>) => StateCreator<S, [], []>;

/**
 * ## Action Organization Approaches
 *
 * ### Approach 1: Inline Actions (Recommended for most cases)
 *
 * Write actions directly in the `createStore` call (shown in the main example above).
 * This is the simplest and most common pattern - keep it inline unless you have a specific
 * reason to extract.
 *
 * ```typescript
 * const [...] = createStore<CounterState, CounterActions>(
 *   { count: 0, name: 'Counter' },
 *   (set, get, store) => ({
 *     increment: () => set(draft => { draft.count += 1; }),
 *     decrement: () => set(draft => { draft.count -= 1; }),
 *     setName: (name: string) => set(draft => { draft.name = name; }),
 *   })
 * );
 * ```
 *
 * ## Advanced: Extracting Action Logic
 *
 * For better code organization in complex stores, you can extract action logic outside
 * the `createStore` call. Choose the approach that best fits your needs:
 *
 * ### Approach 2: Extract State Updaters
 *
 * Extract pure functions that operate on the draft state. Clean, testable, and easy to compose.
 *
 * ```typescript
 * // Outside createStore - pure mutation functions
 * function updateTask(draft: TaskState, task: Task) {
 *   const index = draft.tasks.findIndex(t => t.id === task.id);
 *   if (index !== -1) {
 *     draft.tasks[index] = task;
 *   }
 *   draft.lastUpdated = Date.now();
 * }
 *
 * function clearTaskState(draft: TaskState) {
 *   draft.tasks = [];
 *   draft.filter = 'all';
 *   draft.lastUpdated = null;
 * }
 *
 * // Composition: actions calling other updaters
 * function completeTaskAndNotify(draft: TaskState, taskId: string) {
 *   const task = draft.tasks.find(t => t.id === taskId);
 *   if (task) {
 *     task.completed = true;
 *     draft.notificationSent = true;
 *   }
 * }
 *
 * // Inside createStore
 * const [...] = createStore(initialState, (set, get, store) => ({
 *   updateTask: (task: Task) => set(draft => updateTask(draft, task)),
 *   clearAll: () => set(draft => clearTaskState(draft)),
 *   completeTask: (id: string) => set(draft => completeTaskAndNotify(draft, id)),
 * }));
 * ```
 *
 * ### Approach 3: Extract Action Definitions Object
 *
 * Define all actions in a separate object for organizational clarity. Useful for large stores
 * or when you want to keep the createStore call minimal.
 *
 * ```typescript
 * // Outside createStore - actions definition
 * function defineCartActions(
 *   set: (updater: (draft: CartState) => void) => void,
 *   get: () => CartState,
 *   store: StoreApi<CartState>,
 * ) {
 *   return {
 *     addItem: (item: CartItem) =>
 *       set(draft => { draft.items.push(item); }),
 *
 *     removeItem: (id: string) =>
 *       set(draft => {
 *         draft.items = draft.items.filter(i => i.id !== id);
 *       }),
 *
 *     // Actions can call other actions via nested set calls
 *     clearAndReset: () => {
 *       set(draft => { draft.items = []; }); // Clear items
 *       set(draft => { draft.total = 0; });   // Reset total
 *     },
 *
 *     // Or compose by calling the updaters
 *     checkout: () => {
 *       const current = get();
 *       if (current.items.length === 0) return;
 *
 *       set(draft => {
 *         draft.total = draft.items.reduce((sum, item) => sum + item.price, 0);
 *         draft.checkedOut = true;
 *       });
 *     },
 *   };
 * }
 *
 * // Inside createStore - clean and minimal
 * const [...] = createStore(initialState, defineCartActions);
 * ```
 *
 * ### Composing Actions
 *
 * All approaches support composition - actions calling other actions:
 *
 * ```typescript
 * const [...] = createStore(initialState, (set, get, store) => {
 *   const actions = {
 *     increment: () => set(draft => { draft.count += 1; }),
 *
 *     // Call other actions by referencing them
 *     incrementTwice: () => {
 *       actions.increment();
 *       actions.increment();
 *     },
 *
 *     // Or read state and perform complex logic
 *     smartIncrement: () => {
 *       const current = get();
 *       if (current.count < 100) {
 *         actions.increment(); // Reuse existing action
 *       }
 *     },
 *   };
 *
 *   return actions;
 * });
 * ```
 */

/**
 * Store instance returned by `useStore` hook. Provides full access to store internals for store owners.
 * DO NOT expose this type to consumers - they should use the concrete store's selector hooks instead.
 */
export interface StoreInstance<S, A> extends StoreApi<S> {
  /**
   * Actions object with all mutation functions
   * Stable reference, never causes re-renders when accessed
   */
  actions: A;
}

/**
 * Selector hook for reactive state access
 * Only triggers re-renders when selected data changes
 *
 * @param selector - Function to select data from state
 *
 * By default uses `===` (Object.is) equality.
 */
export interface UseSelector<S> {
  <U>(selector: (state: S) => U): U;
}

/**
 * Provider component props
 */
export interface ProviderProps<S, A> {
  children: React.ReactNode;
  initialState?: Partial<S>;
  /**
   * @deprecated Legacy prop for backwards compatibility only - DO NOT USE in new code or tests!
   *
   * This prop allows passing a pre-created store instance to the Provider, which is ONLY used
   * during migration from Flux/Baobab stores to maintain singleton access patterns (e.g., AxiosStore).
   *
   * **New code should NOT use this prop.** Let the Provider create its own store instance.
   * This ensures proper test isolation and follows React best practices.
   */
  store?: StoreInstance<S, A>;
}

/**
 * Creates a Zustand store with React Context Provider
 *
 * Returns a tuple of [Provider, useStore, useSelector]:
 * - **Provider**: React component that holds a store instance in context
 * - **useStore**: Hook that returns store instance with `.actions`, `.getState()`, `.setState()`, `.subscribe()`
 * - **useSelector**: Hook for reactive state selection (optional)
 *
 * ## Store Owner Responsibilities
 *
 * As a store owner, you have full power:
 * 1. Define state shape and initial values
 * 2. Define actions in the actions factory
 * 3. Create optimized custom hooks for consumers
 * 4. Export only the Provider and custom hooks (not raw access)
 *
 * ## Simple Case (No Actions)
 *
 * ```tsx
 * const [Provider, useStore, useSelector] = createStore({ count: 0 });
 *
 * // ✅ Reactive selector - component re-renders when count changes
 * export const useCount = () => useSelector(state => state.count);
 * export { Provider as CounterProvider };
 * ```
 *
 * ## With Actions
 *
 * ```tsx
 * const [Provider, useStore, useSelector] = createStore(
 *   { count: 0, name: 'Counter' },
 *   (set, get, store) => ({
 *     increment: () => set(draft => { draft.count += 1; }),
 *     setName: (name: string) => set(draft => { draft.name = name; }),
 *   })
 * );
 *
 * // Atomic hooks (returns primitives)
 * export const useCount = () => useSelector(state => state.count);
 * export const useName = () => useSelector(state => state.name);
 *
 * // Custom hooks (normal React composition - just wraps atomic hooks)
 * export const useCounterData = () => {
 *   const count = useCount();
 *   const name = useName();
 *   return { count, name, isValid: count > 0 };
 * };
 *
 * // Actions
 * export const useCounterActions = () => useStore().actions;
 *
 * // Export provider
 * export { Provider as CounterProvider };
 * ```
 *
 * ## Advanced: High-Performance Selectors
 *
 * Use reselect's `createSelector` for expensive computations or derived state:
 *
 * ```tsx
 * import { createSelector } from 'reselect';
 *
 * // Single memoized selector - only recomputes when items or sortOrder changes
 * const expensiveComputationSelector = createSelector(
 *   (s: CounterState) => s.items,
 *   (s: CounterState) => s.sortOrder,
 *   (items, sortOrder) => items.slice().sort(sortOrder)  // Expensive operation!
 * );
 * export const useExpensiveComputation = () => useSelector(expensiveComputationSelector);
 *
 * // Composed selectors - build selector trees by composing selectors
 * // Perfect for multi-stage pipelines (filter → sort → paginate)
 * const firstVisibleIndexSelector = createSelector(
 *   (s: CounterState) => s.scrollTop,
 *   (s: CounterState) => s.itemHeight,
 *   (scrollTop, itemHeight) => Math.floor(scrollTop / itemHeight),
 * );
 *
 * const lastVisibleIndexSelector = createSelector(
 *   (s: CounterState) => s.scrollTop,
 *   (s: CounterState) => s.viewportHeight,
 *   (s: CounterState) => s.itemHeight,
 *   (scrollTop, viewportHeight, itemHeight) => Math.ceil((scrollTop + viewportHeight) / itemHeight),
 * );
 *
 * const visibleItemsSelector = createSelector(
 *   (s: CounterState) => s.items,
 *   firstVisibleIndexSelector,
 *   lastVisibleIndexSelector,
 *   (items, firstIndex, lastIndex) => items.slice(firstIndex, lastIndex + 1),
 * );
 *
 * export const useFirstVisibleIndex = () => useSelector(firstVisibleIndexSelector);
 * export const useLastVisibleIndex = () => useSelector(lastVisibleIndexSelector);
 * export const useVisibleItems = () => useSelector(visibleItemsSelector);
 *
 * // Note: For shallow equality, wrap selector with useShallow:
 * // import { useShallow } from 'zustand/react/shallow';
 * // const value = useSelector(useShallow(s => ({ a: s.a, b: s.b })));
 * // This is an escape hatch - prefer normal React composition or reselect instead.
 * ```
 *
 * @param storeName - Name of the store (without 'Provider' suffix). Used for displayName and error messages.
 *                    Example: "Foo" creates `<FooStoreProvider>` with a "FooStoreProvider" name displayed in the React
 *                    tree, along with a specific error message if the store's hooks are used outside their provider.
 * @param initialState - Initial state (data only, no functions)
 * @param actionsFactory - Optional factory function that returns actions
 * @param middlewares - Optional array of Zustand middlewares to apply
 * @returns Tuple of [Provider component, useStore hook, useSelector hook]
 */
// Overload: with actions and middlewares
export function createStore<S extends object, A extends Record<string, any>>(
  storeName: string,
  initialState: S,
  actionsFactory: ActionsFactory<S, A>,
  middlewares?: Middleware<S>[],
): [(props: ProviderProps<S, A>) => ReactElement | null, () => StoreInstance<S, A>, UseSelector<S>];

// Overload: without actions
export function createStore<S extends object>(
  storeName: string,
  initialState: S,
): [(props: ProviderProps<S, object>) => ReactElement | null, () => StoreInstance<S, object>, UseSelector<S>];

// Implementation
export function createStore<S extends object, A extends Record<string, any>>(
  storeName: string,
  initialState: S,
  actionsFactory?: ActionsFactory<S, A>,
  middlewares?: Middleware<S>[],
): [(props: ProviderProps<S, A>) => ReactElement | null, () => StoreInstance<S, A>, UseSelector<S>] {
  // Clone initial state IMMEDIATELY to prevent external mutations from affecting future Providers
  const clonedInitialState = structuredClone(initialState);

  // Create the context for this store
  const StoreContext = createContext<StoreInstance<S, A> | null>(null);

  // Provider component that creates a store instance
  const Provider = ({ children, initialState: providerInitialState, store: externalStore }: ProviderProps<S, A>) => {
    // Use useState with function initializer for one-time store creation
    const [store] = useState<StoreInstance<S, A>>(() => {
      // Use externally provided store if available (legacy pattern)
      if (externalStore) {
        return externalStore;
      }

      // Otherwise create a new store instance
      const mergedInitialState = {
        ...clonedInitialState,
        ...(providerInitialState ? structuredClone(providerInitialState) : {}),
      };

      return createStoreInstance(mergedInitialState, actionsFactory, middlewares);
    });

    return <StoreContext.Provider value={store}>{children}</StoreContext.Provider>;
  };

  // Set displayName for better debugging in React DevTools
  Provider.displayName = `${storeName}StoreProvider`;

  // Hook to get store instance (for actions, getState, subscribe, etc)
  const useStore = (): StoreInstance<S, A> => {
    const store = useContext(StoreContext);

    if (!store) {
      throw new Error(
        `A hook is attempting to access the ${storeName} store but its companion <${storeName}StoreProvider> can't be found. Did you forget a <${storeName}StoreProvider> around your component?`,
      );
    }

    return store;
  };

  // `useSelector` is a hook which allows you to select state from the store. Only trigger re-renders when selected
  // data changes.
  //
  // IMPORTANT: a selector that returns new objects/arrays MUST use reselect's `createSelector`!
  //
  // Returning primitives or stable references:
  //   const count = useSelector(state => state.count);
  //   const items = useSelector(state => state.items); // even arrays
  //
  // But if you return an object or array you WILL cause React to infinitely re-render. Examples:
  //   const filtered = useSelector(state => state.items.filter(...));
  //   const derived = useSelector(state => ({ isConstant: state.value !== 'some-constant' }));
  //
  // Make sure to use reselect's `createSelector` for derived/computed values:
  //   const selectFiltered = createSelector(
  //     (state) => state.items,
  //     (items) => items.filter(...)
  //   );
  //   const filtered = useSelector(selectFiltered);
  //
  const useSelector = (<U,>(selector: (state: S) => U): U => {
    const store = useStore();
    return useZustandStore(store, selector);
  }) as UseSelector<S>;

  return [Provider, useStore, useSelector];
}

/**
 * Helper to create a store instance outside of React.
 *
 * External usage is ONLY valid in two cases:
 * 1) Tests via the `createTest*Store()` pattern.
 * 2) Legacy singleton access via `legacyCreate*Store()` for app code that needs the store
 *    instance outside of a `<*StoreProvider>` (e.g. interceptors).
 *
 * ## Legacy Use Case (Production)
 *
 * This allows creating a singleton store instance at module load time (like Flux),
 * which can then be passed to the Provider via the `store` prop for backwards compatibility
 * with legacy code that cannot use React hooks (e.g., axios interceptors).
 *
 * @example
 * ```tsx
 * // In some.store.tsx - LEGACY PATTERN ONLY
 * export function legacyCreateSomeStore() {
 *   return createStoreInstance(initialState, actionsFactory);
 * }
 *
 * // In core.stores.ts - LEGACY PATTERN ONLY
 * export const sqSomeStore = legacyCreateSomeStore();
 *
 * // In Main.tsx - LEGACY PATTERN ONLY
 * <AdministrationStoreProvider store={sqAdministrationStore}>
 *   <App />
 * </AdministrationStoreProvider>
 * ```
 */
export function createStoreInstance<S extends object, A extends Record<string, any>>(
  initialState: S,
  actionsFactory?: ActionsFactory<S, A>,
  middlewares?: Middleware<S>[],
): StoreInstance<S, A> {
  // Create store with middlewares
  const baseStateCreator: StateCreator<S, [], []> = (_set, _get, _api) => initialState;

  // Apply middlewares from inside out
  // Note: Typed as `any` because TypeScript can't track type transformations through dynamic middleware composition
  let storeCreator: any = baseStateCreator;

  // First apply immer (innermost)
  storeCreator = immer(storeCreator);

  // Then apply user middlewares from last to first
  if (middlewares && middlewares.length > 0) {
    for (let i = middlewares.length - 1; i >= 0; i--) {
      storeCreator = middlewares[i](storeCreator);
    }
  }

  const store = create<S>()(storeCreator);

  // Create actions if factory provided
  const actions = actionsFactory
    ? actionsFactory(
        // Wrap setState to accept immer-style draft updaters
        // After applying immer middleware, setState's type is transformed to accept (draft: Draft<S>) => void
        // Cast to any because TypeScript can't track the type transformation through middleware
        (updater) => (store.setState as any)(updater),
        () => store.getState(),
        store,
      )
    : ({} as A);

  // Augment store with actions
  return Object.assign(store, { actions });
}
